import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, assetUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CampaignCard, CampaignCardSkeleton } from "@/components/public/CampaignCard";
import { BannerAd, insertBanners } from "@/components/public/BannerAd";
import { BrandMark } from "@/components/BrandMark";
import { CitySelect } from "@/components/CitySelect";
import { BUSINESS_TYPES, BUSINESS_TYPE_ICONS } from "@/lib/businessTypes";
import * as LucideIcons from "lucide-react";
import {
  Search,
  MapPin,
  Sparkles,
  ArrowRight,
  Trophy,
  LogIn,
  Store,
  Building2,
  Rocket,
  QrCode,
  Send,
  ShieldCheck,
  Zap,
  Flame,
  Compass,
} from "lucide-react";

/**
 * CityDooh Homepage — consumer-first Explore experience.
 *
 * Structure (top → bottom):
 *   1. Sticky header (brand + Explore/My Winnings/Vendor Login)
 *   2. Search Hero (headline + keyword + city, client-side filter)
 *   3. Trending Offers strip (first 6 fetched campaigns)
 *   4. Browse by Category (canonical BUSINESS_TYPES, clickable pills)
 *   5. All Live Offers grid (client-filtered)
 *   6. Popular Stores (aggregated by vendor.business)
 *   7. "For Businesses" section (vendor pitch, direct CTAs)
 *   8. Footer
 *
 * Design tokens (strict, must match Login/My Winnings/Offer):
 *   - Gold:  #F5A800
 *   - Ink:   #0A0A0A
 *   - Cream: #F7F4EC
 *   - Anton for headlines (inline font-family so nothing else regresses)
 */
const CREAM = "#F7F4EC";
const INK = "#0A0A0A";
const GOLD = "#F5A800";
const ANTON = '"Anton", "Impact", sans-serif';

export default function MarketingPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null); // null = loading, [] = empty
  const [loadErr, setLoadErr] = useState(null);
  const [query, setQuery] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [activeCategory, setActiveCategory] = useState("");
  // 02/08 — city-scoped category counts. Only populated once the user
  // picks a city — an unscoped count would be misleading given how
  // hyper-local the platform is. Cleared instantly when city is unset.
  const [categoryCounts, setCategoryCounts] = useState({});
  const feedRef = useRef(null);
  const catRef = useRef(null);

  const isCustomerLoggedIn = Boolean(
    typeof window !== "undefined" && localStorage.getItem("citydooh_customer_token"),
  );

  // ── Fetch all publicly-listed campaigns once. Client-side filtering
  //    is intentional (see plan §6.1) — scale is fine and it keeps the
  //    interaction instant. If the collection grows, promote this to
  //    a server-side search endpoint without touching the UI shape.
  useEffect(() => {
    let live = true;
    api.publicCampaigns()
      .then((r) => { if (live) setItems(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (live) setLoadErr("Couldn't load offers right now"); });
    return () => { live = false; };
  }, []);

  // Client-side filter used by both "All Live Offers" and search hero.
  const filtered = useMemo(() => {
    if (!items) return null;
    const q = query.trim().toLowerCase();
    const city = cityFilter.trim().toLowerCase();
    const cat = activeCategory.trim().toLowerCase();
    return items.filter((c) => {
      if (cat) {
        const bt = (c.vendor?.business_type || "").toLowerCase();
        if (bt !== cat) return false;
      }
      if (city) {
        // Effective city — prefer the campaign's own `city` (populated
        // from campaign_city → vendor.city fallback on the server) so
        // homepage filtering matches Explore semantics exactly.
        const ec = (c.city || c.vendor?.city || "").toLowerCase();
        if (ec !== city) return false;
      }
      if (q) {
        const hay = [
          c.name, c.vendor?.business, c.city, c.vendor?.city, c.vendor?.business_type,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, query, cityFilter, activeCategory]);

  // Trending = latest 6 of the CITY/CATEGORY-FILTERED subset. Was
  // previously slicing off the unfiltered `items` list which caused
  // the trending row to keep showing Delhi offers when the visitor
  // selected Bangalore. Reusing `filtered` here means every homepage
  // section (Trending, Categories counts, Live Offers, Popular Stores)
  // moves together when the city filter changes.
  // Note: hard-slice of 6 is retained as a reasonable initial preview
  // window; the ADDITIONAL display cap (city override → global default
  // → uncapped) is layered on top via `displayCaps.trending` below —
  // resolved server-side by /public/homepage-display-caps.
  const trending = useMemo(() => (filtered ? filtered.slice(0, 6) : null), [filtered]);

  // ── Homepage display caps — one shared payload for all 3 sections.
  // Resolution order lives server-side (city_homepage_content override
  // → global_pricing default → uncapped) so the frontend is oblivious
  // to precedence and just applies whatever `.caps` comes back.
  const [displayCaps, setDisplayCaps] = useState({ trending: null, offers: null, stores: null });
  useEffect(() => {
    let live = true;
    api.publicHomepageDisplayCaps(cityFilter.trim() || undefined)
      .then((r) => { if (live) setDisplayCaps(r.data?.caps || {}); })
      .catch(() => { /* silently ignore — falls back to uncapped */ });
    return () => { live = false; };
  }, [cityFilter]);
  // Per-section "See All" expansion — when the visitor clicks the
  // toggle we flip that section's cap off, in-place, no navigation.
  const [expanded, setExpanded] = useState({ trending: false, offers: false, stores: false });

  // Popular Stores aggregation — same city/category scope as the
  // rest of the page. If the filtered subset is empty we return an
  // empty list so the section renders its "nothing here" state
  // instead of misleadingly showing stores from other cities.
  //
  // 03/08 — When a city is selected we prefer the server-side
  // admin-curated feed (respects pins/hides). Otherwise fall back to
  // the local in-memory aggregation over the filtered items.
  const [curatedStores, setCuratedStores] = useState(null);
  useEffect(() => {
    if (!cityFilter.trim()) { setCuratedStores(null); return; }
    let live = true;
    api.publicPopularStores(cityFilter.trim(), 8)
      .then((r) => { if (live) setCuratedStores(r.data?.stores || []); })
      .catch(() => { if (live) setCuratedStores(null); });
    return () => { live = false; };
  }, [cityFilter]);

  // City homepage content — franchise/admin-curated feed layered on
  // top of the automatic sections (Part 4/5). Fetch only when the
  // user has actively picked a city; no city → skip cleanly so new
  // cities still see the fully automatic homepage.
  const [cityContent, setCityContent] = useState(null);
  useEffect(() => {
    if (!cityFilter.trim()) { setCityContent(null); return; }
    let live = true;
    api.publicCityHomepage(cityFilter.trim())
      .then((r) => { if (live) setCityContent(r.data || null); })
      .catch(() => { if (live) setCityContent(null); });
    return () => { live = false; };
  }, [cityFilter]);

  const popularStores = useMemo(() => {
    if (curatedStores && curatedStores.length > 0) {
      return curatedStores.map((s) => ({
        key: s.vendor_id,
        business: s.business,
        city: s.city,
        business_type: s.business_type,
        logo_url: s.logo_url,
        count: s.count,
        campaigns: [],
      }));
    }
    if (!filtered || filtered.length === 0) return [];
    const byVendor = new Map();
    for (const c of filtered) {
      const key = c.vendor?.vendor_id || c.vendor?.business || "unknown";
      const existing = byVendor.get(key);
      // 03/08 — fall back through every logo source the API exposes so
      // vendors with a default_logo_url set at profile level (but no
      // per-campaign logo) still get their mark on the store card.
      const logoUrl = c.logo_url || c.vendor?.default_logo_url || c.vendor?.logo_url || "";
      if (existing) {
        existing.count += 1;
        existing.campaigns.push(c);
        // Keep the first non-empty logo we see for this vendor.
        if (!existing.logo_url && logoUrl) existing.logo_url = logoUrl;
      } else {
        byVendor.set(key, {
          key,
          business: c.vendor?.business || "Local business",
          city: c.vendor?.city || "",
          business_type: c.vendor?.business_type || "",
          logo_url: logoUrl,
          count: 1,
          campaigns: [c],
        });
      }
    }
    return Array.from(byVendor.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [filtered, curatedStores]);

  const openStore = (store) => {
    // Send them into the feed with the vendor's category pre-filtered
    // — a lightweight "store page" surrogate until real vendor
    // landing pages ship.
    if (store.business) setQuery(store.business);
    setActiveCategory("");
    feedRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleCategory = (cat) => {
    setActiveCategory(activeCategory === cat ? "" : cat);
    // Scroll the feed into view so users immediately see the filtered
    // grid instead of staring at the top of the page.
    setTimeout(() => feedRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 40);
  };

  return (
    <div className="min-h-screen bg-[#F7F4EC] text-[#0A0A0A]" data-testid="homepage-root">
      {/* ───────────────────────── HEADER ───────────────────────── */}
      <header className="sticky top-0 z-40 border-b-2 border-[#0A0A0A] bg-[#F7F4EC]/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-[72px] md:h-[88px] lg:h-24 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0A0A0A] rounded-md" aria-label="City DOOH home">
            <BrandMark size="header" data-testid="home-brand" />
          </Link>

          <nav className="hidden md:flex items-center gap-6 text-xs font-bold uppercase tracking-widest">
            <a href="#categories" className="hover:text-[#F5A800] transition-colors" data-testid="home-nav-categories">
              Categories
            </a>
            <a href="#offers" className="hover:text-[#F5A800] transition-colors" data-testid="home-nav-offers">
              Offers
            </a>
            <a href="#stores" className="hover:text-[#F5A800] transition-colors" data-testid="home-nav-stores">
              Stores
            </a>
            <Link to="/for-business" className="hover:text-[#F5A800] transition-colors" data-testid="home-nav-forbusiness">
              For Business
            </Link>
          </nav>

          <div className="flex items-center gap-2">
            {isCustomerLoggedIn ? (
              <Button
                variant="outline"
                className="h-11 md:h-9 border-2 border-[#0A0A0A] text-[#0A0A0A] font-bold uppercase tracking-widest text-xs hover:bg-[#F5A800] hover:text-[#0A0A0A] hover:border-[#0A0A0A]"
                onClick={() => navigate("/customer/my-winnings")}
                data-testid="home-my-winnings"
              >
                <Trophy className="h-3.5 w-3.5 mr-1.5" /> My Winnings
              </Button>
            ) : (
              <Button
                variant="outline"
                className="h-11 md:h-9 border-2 border-[#0A0A0A] text-[#0A0A0A] font-bold uppercase tracking-widest text-xs hover:bg-[#F5A800] hover:text-[#0A0A0A] hover:border-[#0A0A0A]"
                onClick={() => navigate("/customer/login")}
                data-testid="home-customer-signin"
              >
                <LogIn className="h-3.5 w-3.5 mr-1.5" /> Sign in
              </Button>
            )}
            <Link
              to="/explore"
              className="hidden sm:inline-flex items-center h-11 md:h-9 px-3 rounded-md border-2 border-[#0A0A0A] bg-[#0A0A0A] text-[#F5A800] font-bold uppercase tracking-widest text-xs hover:bg-[#F5A800] hover:text-[#0A0A0A] transition-colors"
              data-testid="home-explore-cta"
            >
              <Compass className="h-3.5 w-3.5 mr-1.5" /> Explore
            </Link>
          </div>
        </div>
      </header>

      {/* ───────────────────────── HERO ───────────────────────── */}
      <section className="relative overflow-hidden border-b-2 border-[#0A0A0A]">
        {/* Editorial gold blob accent — kept subtle to respect the
            <20% gradient rule. */}
        <div
          aria-hidden
          className="absolute -top-24 -right-16 w-[420px] h-[420px] rounded-full opacity-25 blur-3xl"
          style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 60%)` }}
        />
        <div
          aria-hidden
          className="absolute -bottom-24 -left-24 w-[360px] h-[360px] rounded-full opacity-20 blur-3xl"
          style={{ background: `radial-gradient(circle, ${INK} 0%, transparent 60%)` }}
        />

        <div className="max-w-6xl mx-auto px-4 md:px-6 py-14 md:py-20 relative">
          <div className="max-w-3xl">
            <span
              className="inline-flex items-center gap-1.5 text-[10px] md:text-xs font-black uppercase tracking-[0.25em] text-[#0A0A0A] bg-[#F5A800] border-2 border-[#0A0A0A] px-3 py-1.5 rounded-full"
              data-testid="home-hero-eyebrow"
            >
              <Sparkles className="h-3 w-3" /> Real rewards from real local shops
            </span>
            <h1
              className="mt-5 text-5xl sm:text-6xl md:text-7xl leading-[0.9] uppercase tracking-tight"
              style={{ fontFamily: ANTON }}
              data-testid="home-hero-title"
            >
              WIN REAL REWARDS<br />
              FROM <span className="text-[#F5A800]">LOCAL BUSINESSES</span><br />
              NEAR YOU.
            </h1>
            <p className="mt-5 text-base md:text-lg text-[#0A0A0A]/75 max-w-2xl">
              Spin the wheel, scratch the card, or grab an instant offer — no accounts, no clutter.
              Just tap and win from the shops on your street.
            </p>
          </div>

          {/* Search hero card */}
          <div
            className="mt-8 md:mt-10 bg-white border-2 border-[#0A0A0A] rounded-2xl p-3 md:p-4 flex flex-col md:flex-row md:items-center gap-2"
            style={{ boxShadow: "6px 6px 0 #0A0A0A" }}
            data-testid="home-search-card"
          >
            <div className="flex-1 flex items-center gap-2 px-2">
              <Search className="h-5 w-5 shrink-0 text-[#0A0A0A]/60" />
              <Input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search offers, shops or categories…"
                className="h-11 md:h-12 border-0 shadow-none focus-visible:ring-0 text-base"
                data-testid="home-search-input"
              />
            </div>
            <div className="hidden md:block w-px self-stretch bg-[#0A0A0A]/20" />
            <div className="flex-1 md:max-w-xs flex items-center gap-2 px-2 border-t md:border-t-0 border-[#0A0A0A]/10 pt-2 md:pt-0">
              <CitySelect
                value={cityFilter}
                onChange={setCityFilter}
                placeholder="Your city"
                triggerClassName="h-11 md:h-12 border-0 shadow-none justify-start"
                testIdPrefix="home-hero-city"
              />
            </div>
            <Button
              className="h-11 md:h-12 px-5 md:px-6 font-black uppercase tracking-widest text-sm bg-[#0A0A0A] text-[#F5A800] border-2 border-[#0A0A0A] hover:bg-[#F5A800] hover:text-[#0A0A0A] transition-colors"
              style={{ fontFamily: ANTON }}
              onClick={() => feedRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
              data-testid="home-search-submit"
            >
              Find Offers <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </div>

          {/* Quick stats row */}
          <div className="mt-6 md:mt-8 grid grid-cols-3 gap-3 md:gap-6 max-w-2xl">
            <StatChip label="Live offers" value={items ? String(items.length) : "…"} testid="home-stat-live" />
            <StatChip label="Categories" value={String(BUSINESS_TYPES.length)} testid="home-stat-cat" />
            <StatChip label="Cost to play" value="₹0" testid="home-stat-cost" />
          </div>

          {/* Homepage-top banner (Section B / Part 4). Only renders
              when an active banner exists for the zone — otherwise
              collapses to nothing so the layout stays clean at low
              inventory. Sits below the stats row, above "Trending now". */}
          <div className="mt-6 md:mt-8 max-w-3xl">
            <BannerAd zone="homepage_top" />
          </div>
        </div>
      </section>

      {/* ─────────────── FEATURED IN {CITY} (curated) ─────────────── */}
      {cityContent && (cityContent.curated_campaigns?.length > 0 || cityContent.cover_banner_url) && (
        <section className="max-w-7xl mx-auto px-4 md:px-6 pt-12 md:pt-14" data-testid="home-city-featured">
          <SectionHead
            eyebrow={<><Flame className="h-3 w-3" /> Featured in {cityContent.city}</>}
            title={`Hand-picked for ${cityContent.city}`}
            subtitle={`Curated by your local City DOOH team.`}
          />
          {cityContent.cover_banner_url && (
            <div className="mt-6 rounded-2xl overflow-hidden border-2 border-[#0A0A0A] shadow-[6px_6px_0_#0A0A0A]">
              <img
                src={cityContent.cover_banner_url}
                alt={`${cityContent.city} cover banner`}
                className="block w-full h-auto object-cover"
              />
            </div>
          )}
          {cityContent.curated_campaigns?.length > 0 && (
            <div className="mt-6 md:mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-testid="home-city-featured-grid">
              {cityContent.curated_campaigns.map((c) => (
                <CampaignCard key={c.id} campaign={c} size="lg" />
              ))}
            </div>
          )}
        </section>
      )}

      {/* ───────────────────────── TRENDING ───────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-16">
        <SectionHead
          eyebrow={<><Flame className="h-3 w-3" /> Trending now</>}
          title="Hot offers this week"
          subtitle="Fresh rewards from local businesses — updated live."
          right={
            <Link
              to="/explore"
              className="text-xs font-bold uppercase tracking-widest hover:text-[#F5A800] inline-flex items-center gap-1"
              data-testid="home-trending-viewall"
            >
              See all <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          }
        />

        <div className="mt-6 md:mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-testid="home-trending-grid">
          {trending === null && [1, 2, 3, 4, 5, 6].map((i) => <CampaignCardSkeleton key={i} size="lg" />)}
          {trending && trending.length === 0 && (
            <EmptyState message="No trending offers yet. Check back soon." />
          )}
          {trending && trending.length > 0 && (() => {
            // Apply display cap; when the visitor toggles "See all"
            // we render the full slice (no navigation).
            const cap = displayCaps.trending;
            const shown = (cap == null || expanded.trending) ? trending : trending.slice(0, cap);
            return shown.map((c) => <CampaignCard key={c.id} campaign={c} size="lg" />);
          })()}
        </div>
        {trending && trending.length > 0 && displayCaps.trending != null && trending.length > displayCaps.trending && (
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => setExpanded((e) => ({ ...e, trending: !e.trending }))}
              className="text-sm font-bold underline underline-offset-2"
              data-testid="home-trending-see-all"
            >
              {expanded.trending ? "Show less" : `See all ${trending.length}`}
            </button>
          </div>
        )}
      </section>

      {/* ───────────────────────── CATEGORIES ───────────────────────── */}
      <section id="categories" ref={catRef} className="border-y-2 border-[#0A0A0A] bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-16">
          <SectionHead
            dark
            eyebrow={<><Store className="h-3 w-3" /> Browse by category</>}
            title="What are you in the mood for?"
            subtitle="Tap a category to see every live offer in that space."
          />
          <div
            className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4"
            data-testid="home-categories-grid"
          >
            {BUSINESS_TYPES.map((cat) => {
              const iconName = BUSINESS_TYPE_ICONS[cat] || "Store";
              const Icon = LucideIcons[iconName] || LucideIcons.Store;
              const active = activeCategory === cat;
              // 02/08 — count only appears once a city is picked (see
              // spec: "unscoped/national count would be misleading").
              const hasCityFilter = !!cityFilter.trim();
              const count = hasCityFilter ? (categoryCounts[cat] || 0) : null;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => handleCategory(cat)}
                  className={`group relative flex flex-col items-center gap-2 text-center rounded-2xl border-2 p-4 md:p-5 transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F5A800] ${
                    active
                      ? "bg-[#F5A800] text-[#0A0A0A] border-[#F5A800]"
                      : "bg-[#0A0A0A] text-white border-white/20 hover:border-[#F5A800] hover:-translate-y-0.5"
                  }`}
                  data-testid={`home-category-${cat.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <Icon className="h-6 w-6 md:h-7 md:w-7" strokeWidth={2} />
                  {/* 02/08 — readability fix: use Anton with a more
                      generous letter-spacing + snug line-height, and
                      drop the tracking-wider that was fighting the
                      inline letterSpacing. Sizes bumped up half a step
                      on mobile. */}
                  <span
                    className="text-[13px] md:text-sm leading-snug uppercase font-black"
                    style={{ fontFamily: ANTON, letterSpacing: "0.08em" }}
                  >
                    {cat}
                  </span>
                  {count != null && count > 0 && (
                    <span
                      className={`absolute top-2 right-2 text-[9px] font-black px-1.5 py-0.5 rounded-full ${
                        active ? "bg-[#0A0A0A] text-[#F5A800]" : "bg-[#F5A800] text-[#0A0A0A]"
                      }`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {activeCategory && (
            <p className="mt-6 text-sm text-white/70 flex items-center gap-3" data-testid="home-active-filter">
              Filtering by <Badge className="bg-[#F5A800] text-[#0A0A0A] hover:bg-[#F5A800]">{activeCategory}</Badge>
              <button
                onClick={() => setActiveCategory("")}
                className="underline text-white/80 hover:text-[#F5A800]"
              >
                Clear
              </button>
            </p>
          )}
        </div>
      </section>

      {/* ───────────────────────── ALL LIVE OFFERS ───────────────────────── */}
      <section id="offers" ref={feedRef} className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-16">
        <SectionHead
          eyebrow={<><Zap className="h-3 w-3" /> All live offers</>}
          title={activeCategory ? `${activeCategory} offers` : "Every live offer in one feed"}
          subtitle={
            filtered && items
              ? `${filtered.length} of ${items.length} offers match your filters.`
              : "Loading the latest live campaigns…"
          }
          right={
            (query || cityFilter || activeCategory) && (
              <button
                onClick={() => { setQuery(""); setCityFilter(""); setActiveCategory(""); }}
                className="text-xs font-bold uppercase tracking-widest hover:text-[#F5A800] underline"
                data-testid="home-feed-clear"
              >
                Reset filters
              </button>
            )
          }
        />

        <div className="mt-6 md:mt-8">
          {loadErr && (
            <div className="rounded-2xl border-2 border-[#0A0A0A] bg-white p-8 text-center" style={{ boxShadow: "4px 4px 0 #0A0A0A" }}>
              <p className="font-bold">{loadErr}</p>
              <p className="text-sm text-[#0A0A0A]/60 mt-1">Please refresh in a moment.</p>
            </div>
          )}
          {!loadErr && filtered === null && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => <CampaignCardSkeleton key={i} />)}
            </div>
          )}
          {!loadErr && filtered && filtered.length === 0 && (
            <EmptyState
              message={
                query || cityFilter || activeCategory
                  ? "No offers match your filters. Try adjusting your search."
                  : "No live offers yet — new ones drop every week."
              }
            />
          )}
          {!loadErr && filtered && filtered.length > 0 && (() => {
            const cap = displayCaps.offers;
            const shown = (cap == null || expanded.offers) ? filtered : filtered.slice(0, cap);
            return (
              <>
                <div
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6"
                  data-testid="home-feed-grid"
                >
                  {insertBanners(shown, "between_offers", { every: 6, minReal: 6 }).map((item) =>
                    item.kind === "card"
                      ? <CampaignCard key={item.key} campaign={item.data} />
                      : <BannerAd key={item.key} zone={item.zone} compact className="rounded-2xl" />
                  )}
                </div>
                {cap != null && filtered.length > cap && (
                  <div className="mt-4 text-center">
                    <button
                      type="button"
                      onClick={() => setExpanded((e) => ({ ...e, offers: !e.offers }))}
                      className="text-sm font-bold underline underline-offset-2"
                      data-testid="home-offers-see-all"
                    >
                      {expanded.offers ? "Show less" : `See all ${filtered.length}`}
                    </button>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </section>

      {/* ───────────────────────── POPULAR STORES ───────────────────────── */}
      <section id="stores" className="border-t-2 border-[#0A0A0A] bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-16">
          <SectionHead
            eyebrow={<><Building2 className="h-3 w-3" /> Popular stores</>}
            title="Businesses everyone's rewarding"
            subtitle="Shops running the most campaigns right now."
          />
          <div className="mt-6 md:mt-8">
            {popularStores.length === 0 ? (
              <EmptyState message="Popular stores will appear here once campaigns go live." />
            ) : (() => {
              const cap = displayCaps.stores;
              const shown = (cap == null || expanded.stores) ? popularStores : popularStores.slice(0, cap);
              return (
                <>
                  <div
                    className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5"
                    data-testid="home-stores-grid"
                  >
                    {insertBanners(shown, "between_stores", { every: 6, minReal: 6 }).map((item) => {
                      if (item.kind === "banner") {
                        return <BannerAd key={item.key} zone={item.zone} compact className="rounded-2xl" />;
                      }
                      const s = item.data;
                      return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => openStore(s)}
                    className="text-left rounded-2xl border-2 border-[#0A0A0A] bg-[#F7F4EC] p-4 md:p-5 transition-all duration-150 hover:-translate-y-0.5 hover:shadow-[6px_6px_0_#F5A800] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F5A800]"
                    style={{ boxShadow: "4px 4px 0 #0A0A0A" }}
                    data-testid={`home-store-card`}
                  >
                    <div className="h-11 w-11 rounded-lg bg-[#0A0A0A] text-[#F5A800] flex items-center justify-center border-2 border-[#0A0A0A] overflow-hidden">
                      {s.logo_url ? (
                        <img
                          src={assetUrl(s.logo_url)}
                          alt=""
                          className="w-full h-full object-contain bg-white"
                          loading="lazy"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                          data-testid="home-store-logo"
                        />
                      ) : (
                        <span
                          className="text-lg leading-none"
                          style={{ fontFamily: ANTON }}
                        >
                          {(s.business || "?").slice(0, 1).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <p
                      className="mt-3 text-base md:text-lg leading-tight uppercase truncate"
                      style={{ fontFamily: ANTON }}
                    >
                      {s.business}
                    </p>
                    <p className="text-xs text-[#0A0A0A]/60 truncate">
                      {s.business_type || "Local business"}
                      {s.city ? ` · ${s.city}` : ""}
                    </p>
                    <p className="mt-3 text-[10px] font-black uppercase tracking-widest text-[#F5A800] bg-[#0A0A0A] inline-block px-2 py-0.5 rounded-full">
                      {s.count} live offer{s.count === 1 ? "" : "s"}
                    </p>
                  </button>
                  );
                })}
              </div>
              {cap != null && popularStores.length > cap && (
                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={() => setExpanded((e) => ({ ...e, stores: !e.stores }))}
                    className="text-sm font-bold underline underline-offset-2"
                    data-testid="home-stores-see-all"
                  >
                    {expanded.stores ? "Show less" : `See all ${popularStores.length}`}
                  </button>
                </div>
              )}
              </>
              );
            })()}
          </div>
        </div>
      </section>

      {/* ───────────────────────── FOR BUSINESSES ───────────────────────── */}
      <section id="for-business" className="border-t-2 border-[#0A0A0A] bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20 relative">
          <div
            aria-hidden
            className="absolute top-1/2 right-8 -translate-y-1/2 w-56 h-56 rounded-full opacity-30 blur-3xl hidden md:block"
            style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 60%)` }}
          />
          <div className="max-w-4xl relative">
            <span
              className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.25em] px-3 py-1.5 rounded-full bg-[#F5A800] text-[#0A0A0A] border-2 border-[#F5A800]"
              data-testid="home-forbiz-eyebrow"
            >
              <Rocket className="h-3 w-3" /> For Businesses
            </span>
            <h2
              className="mt-5 text-4xl sm:text-5xl md:text-6xl leading-[0.9] uppercase tracking-tight"
              style={{ fontFamily: ANTON }}
            >
              Turn your shopfront into a<br />
              <span className="text-[#F5A800]">lead-generating game.</span>
            </h2>
            <p className="mt-5 text-base md:text-lg text-white/75 max-w-2xl">
              Launch a spin wheel, scratch card, or instant offer in minutes. Collect verified leads,
              hand out server-decided prizes, then retarget by WhatsApp and email. ₹499 per campaign —
              no subscription.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/vendor/register" data-testid="home-forbiz-get-started">
                <Button
                  className="h-12 px-6 text-sm font-black uppercase tracking-widest bg-[#F5A800] text-[#0A0A0A] border-2 border-[#F5A800] hover:bg-white hover:text-[#0A0A0A] hover:border-white transition-colors"
                  style={{ fontFamily: ANTON }}
                >
                  Get Started <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </Link>
              <Link to="/vendor/login" data-testid="home-forbiz-login">
                <Button
                  variant="outline"
                  className="h-12 px-6 text-sm font-black uppercase tracking-widest bg-transparent text-white border-2 border-white hover:bg-white hover:text-[#0A0A0A] transition-colors"
                  style={{ fontFamily: ANTON }}
                >
                  Vendor Login
                </Button>
              </Link>
              <a
                href="#pricing"
                className="text-xs font-bold uppercase tracking-widest text-white/80 hover:text-[#F5A800] inline-flex items-center gap-1"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
                }}
                data-testid="home-forbiz-pricing"
              >
                See pricing <ArrowRight className="h-3.5 w-3.5" />
              </a>
              <a
                href="#how"
                className="text-xs font-bold uppercase tracking-widest text-white/80 hover:text-[#F5A800] inline-flex items-center gap-1"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("how")?.scrollIntoView({ behavior: "smooth" });
                }}
                data-testid="home-forbiz-how"
              >
                How it works <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </div>

            {/* How it works — compact 3-step summary */}
            <div id="how" className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              <StepCard step="1" icon={<Rocket className="h-5 w-5" />} title="Build your campaign" desc="Pick a game type, set prizes, define win probabilities. Live preview as you build." />
              <StepCard step="2" icon={<QrCode className="h-5 w-5" />} title="Share QR or link" desc="Every campaign ships with a downloadable QR + shareable URL for storefront, flyers, socials." />
              <StepCard step="3" icon={<Send className="h-5 w-5" />} title="Retarget & convert" desc="Verified leads become an audience. Bring them back with email + WhatsApp reminders." />
            </div>

            {/* Pricing — compact grid */}
            <div id="pricing" className="mt-14">
              <p
                className="text-[10px] font-black uppercase tracking-[0.25em] text-[#F5A800] mb-2"
              >
                Simple pricing
              </p>
              <h3
                className="text-3xl md:text-4xl uppercase leading-none"
                style={{ fontFamily: ANTON }}
              >
                Pay only for what works.
              </h3>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <PriceCard
                  title="Free Trial"
                  amount="Free"
                  suffix="15 days"
                  bullets={["Up to 300 leads", "Full customization", "No card required"]}
                />
                <PriceCard
                  title="Per Campaign"
                  amount="₹499"
                  suffix="one-time"
                  highlight
                  bullets={["Unlimited plays", "QR + share link", "Full analytics"]}
                />
                <PriceCard
                  title="Retargeting"
                  amount="₹0.15"
                  suffix="/ email"
                  bullets={["₹1.75 / WhatsApp msg", "Verified leads only", "Delivery tracking"]}
                />
              </div>
              <p className="mt-3 text-xs text-white/50">All pricing excludes 18% GST. WhatsApp retargeting launching soon.</p>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-4 text-xs text-white/60">
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-[#F5A800]" /> Server-decided wins
              </span>
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-[#F5A800]" /> Vendor-isolated data
              </span>
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-[#F5A800]" /> Cryptographic coupons
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────────────── FOOTER ───────────────────────── */}
      <footer className="bg-[#0A0A0A] text-white/70 border-t-2 border-[#F5A800]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <BrandMark onDark size="footer" data-testid="footer-brand" />
            <p className="mt-3 text-xs text-white/50 max-w-md">
              India&apos;s gamified marketing platform — spin, scratch, or claim real rewards from local businesses.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 sm:gap-6 text-xs font-bold uppercase tracking-widest">
            <Link to="/explore" className="hover:text-[#F5A800]" data-testid="footer-link-explore">Explore</Link>
            <Link to="/customer/login" className="hover:text-[#F5A800]" data-testid="footer-link-signin">Sign in</Link>
            <Link to="/for-business" className="hover:text-[#F5A800]" data-testid="footer-link-forbusiness">For Businesses</Link>
            <Link to="/vendor/login" className="hover:text-[#F5A800]" data-testid="footer-link-vendor">Vendor</Link>
            <Link to="/contact" className="hover:text-[#F5A800]" data-testid="footer-link-contact">Contact</Link>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-5 text-xs flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <span>© {new Date().getFullYear()} Shining Dooh LLP</span>
            <span>
              Created by{" "}
              <a
                href="https://www.shineadagency.com"
                target="_blank"
                rel="noreferrer"
                className="text-[#F5A800] hover:underline font-semibold"
                data-testid="footer-shine-credit"
              >
                SHINE AD AGENCY
              </a>
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ─────────────────────── Reusable sub-components ─────────────────────── */

function SectionHead({ eyebrow, title, subtitle, right, dark = false }) {
  return (
    <div className="flex items-end justify-between gap-4 flex-wrap">
      <div className="max-w-2xl">
        <span
          className={`inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.25em] px-2.5 py-1 rounded-full border-2 ${
            dark ? "bg-[#F5A800] text-[#0A0A0A] border-[#F5A800]" : "bg-[#0A0A0A] text-[#F5A800] border-[#0A0A0A]"
          }`}
        >
          {eyebrow}
        </span>
        <h2
          className={`mt-3 text-3xl sm:text-4xl md:text-5xl uppercase leading-[0.95] tracking-tight ${dark ? "text-white" : "text-[#0A0A0A]"}`}
          style={{ fontFamily: ANTON }}
        >
          {title}
        </h2>
        {subtitle && (
          <p className={`mt-2 text-sm md:text-base ${dark ? "text-white/70" : "text-[#0A0A0A]/70"}`}>
            {subtitle}
          </p>
        )}
      </div>
      {right && <div className="shrink-0">{right}</div>}
    </div>
  );
}

function StatChip({ label, value, testid }) {
  return (
    <div
      className="bg-white border-2 border-[#0A0A0A] rounded-xl px-3 py-2 md:px-4 md:py-3"
      style={{ boxShadow: "3px 3px 0 #0A0A0A" }}
      data-testid={testid}
    >
      <p
        className="text-2xl md:text-3xl leading-none"
        style={{ fontFamily: ANTON }}
      >
        {value}
      </p>
      <p className="text-[10px] md:text-xs uppercase tracking-widest text-[#0A0A0A]/60 mt-0.5">{label}</p>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div
      className="rounded-2xl border-2 border-dashed border-[#0A0A0A]/40 bg-white p-10 text-center"
      data-testid="home-empty-state"
    >
      <p className="font-bold text-[#0A0A0A]" style={{ fontFamily: ANTON }}>{message}</p>
    </div>
  );
}

function StepCard({ step, icon, title, desc }) {
  return (
    <div className="rounded-2xl border-2 border-white/25 bg-white/5 p-5">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-[#F5A800] text-[#0A0A0A] flex items-center justify-center border-2 border-[#F5A800]">
          {icon}
        </div>
        <span
          className="text-3xl leading-none text-[#F5A800]"
          style={{ fontFamily: ANTON }}
        >
          {step}
        </span>
      </div>
      <h3
        className="mt-4 text-xl uppercase leading-tight"
        style={{ fontFamily: ANTON }}
      >
        {title}
      </h3>
      <p className="mt-1.5 text-sm text-white/70 leading-relaxed">{desc}</p>
    </div>
  );
}

function PriceCard({ title, amount, suffix, bullets = [], highlight = false }) {
  return (
    <div
      className={`rounded-2xl border-2 p-5 ${
        highlight
          ? "bg-[#F5A800] text-[#0A0A0A] border-[#F5A800]"
          : "bg-white/5 text-white border-white/25"
      }`}
    >
      <p
        className={`text-xs font-black uppercase tracking-widest ${
          highlight ? "text-[#0A0A0A]/70" : "text-[#F5A800]"
        }`}
      >
        {title}
      </p>
      <p
        className="mt-2 text-4xl leading-none"
        style={{ fontFamily: ANTON }}
      >
        {amount}
        <span
          className={`ml-2 align-baseline text-xs font-bold uppercase tracking-widest ${
            highlight ? "text-[#0A0A0A]/60" : "text-white/60"
          }`}
        >
          {suffix}
        </span>
      </p>
      <ul className="mt-4 space-y-1.5 text-sm">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-2">
            <span
              className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${
                highlight ? "bg-[#0A0A0A]" : "bg-[#F5A800]"
              }`}
            />
            <span className={highlight ? "text-[#0A0A0A]/85" : "text-white/85"}>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
